import { useEffect, useState } from "react";
import api, { formatTL } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, AlertTriangle, Pencil, Trash2, Package, Search } from "lucide-react";
import { ConfirmDialog } from "../../components/common";
import { toast } from "sonner";

const empty = { name: "", sku: "", price: 0, cost: 0, stock_qty: 0, min_stock: 0, unit: "adet", supplier: "", supplier_phone: "", is_active: true };

export default function SpareParts() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [stockF, setStockF] = useState("all");
  const [del, setDel] = useState(null);

  const load = async () => { const { data } = await api.get("/parts"); setList(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("Parça adı zorunlu"); return; }
    const payload = {
      ...form,
      price: Number(form.price), cost: Number(form.cost),
      stock_qty: Number(form.stock_qty), min_stock: Number(form.min_stock),
    };
    try {
      if (editId) await api.put(`/parts/${editId}`, payload);
      else await api.post("/parts", payload);
      toast.success("Kaydedildi"); setOpen(false); setEditId(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const remove = async () => {
    await api.delete(`/parts/${del.id}`);
    toast.success("Silindi"); setDel(null); load();
  };
  const shown = list.filter((p) => {
    if (q && !`${p.name} ${p.sku || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    const low = p.stock_qty <= p.min_stock && p.min_stock > 0;
    if (stockF === "low" && !low) return false;
    if (stockF === "out" && p.stock_qty > 0) return false;
    if (stockF === "inactive" && p.is_active !== false) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="parts-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Stok Yönetimi</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">Yedek Parça</h1>
        </div>
        <Button onClick={() => { setEditId(null); setForm(empty); setOpen(true); }} data-testid="new-part-button" className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-2" /> Yeni Parça
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative w-full sm:w-72">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Parça adı veya SKU ara" className="pl-9" data-testid="part-search" />
        </div>
        {[["all", "Tümü"], ["low", "Kritik stok"], ["out", "Tükenen"], ["inactive", "Pasif"]].map(([k, l]) => (
          <Button key={k} size="sm" variant={stockF === k ? "default" : "outline"} onClick={() => setStockF(k)} data-testid={`part-filter-${k}`}>{l}</Button>
        ))}
        <span className="text-xs text-slate-500">{shown.length} / {list.length}</span>
      </div>

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 font-semibold">Parça</th>
              <th className="px-4 py-3 font-semibold">SKU</th>
              <th className="px-4 py-3 font-semibold text-right">Stok</th>
              <th className="px-4 py-3 font-semibold text-right">Min</th>
              <th className="px-4 py-3 font-semibold text-right">Maliyet</th>
              <th className="px-4 py-3 font-semibold text-right">Fiyat</th>
              <th className="px-4 py-3 font-semibold w-24"></th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="text-center py-12">
              <Package className="h-10 w-10 text-slate-300 mx-auto mb-2" />
              <div className="text-slate-500">Yedek parça yok</div>
            </td></tr>}
            {shown.map(p => {
              const low = p.stock_qty <= p.min_stock && p.min_stock > 0;
              return (
                <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-medium text-slate-900">{p.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{p.sku || "-"}</td>
                  <td className={`px-4 py-3 text-right font-mono font-semibold ${low ? "text-amber-700" : "text-slate-900"}`}>
                    {low && <AlertTriangle className="inline h-3.5 w-3.5 mr-1 text-amber-600" />}
                    {p.stock_qty} {p.unit}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-500">{p.min_stock}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-600">{formatTL(p.cost)}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-900 font-semibold">{formatTL(p.price)}</td>
                  <td className="px-4 py-3 flex gap-1 justify-end">
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => { setEditId(p.id); setForm({ ...empty, ...p }); setOpen(true); }}><Pencil className="h-3 w-3" /></Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-rose-600" onClick={() => setDel(p)} data-testid={`delete-part-${p.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Parça Düzenle" : "Yeni Parça"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Parça Adı *</Label><Input data-testid="part-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Maliyet</Label><Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
              <div><Label>Satış</Label><Input type="number" data-testid="part-price-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
              <div><Label>Birim</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Stok</Label><Input type="number" data-testid="part-stock-input" value={form.stock_qty} onChange={(e) => setForm({ ...form, stock_qty: e.target.value })} /></div>
              <div><Label>Min Stok</Label><Input type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tedarikçi</Label><Input data-testid="part-supplier" value={form.supplier || ""} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
              <div><Label>Tedarikçi telefon</Label><Input value={form.supplier_phone || ""} onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })} /></div>
            </div>
            <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} /><Label>Aktif</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} data-testid="save-part-button" className="bg-blue-600 hover:bg-blue-700">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!del} onOpenChange={(v) => !v && setDel(null)} title={`${del?.name} silinsin mi?`} description="Parça stok listesinden kalıcı olarak silinir." onConfirm={remove} />
    </div>
  );
}
